import { orderBy } from 'lodash-es';
import sortBy from 'lodash-es/sortBy';
import { IDealer } from '.';
import { IState } from '..';
import { IOption } from '../../components/Select';
import { FilterTypes, getFilteredSalesDistricts, getFilters, ISalesDealersFilters } from '../filters';
import { IShipmentListItem } from '../logistics';
import { FinancialType } from '../lookups';
import { IOrderMin } from '../order/orderInterfaces';
import { IProgramGroup } from '../programs';
import { IDealerFinancialSummary, IDealerMin, IPowerAssessment } from './dealerInterfaces';
import { DealerLoadingTypes } from './dealerReducer';

export function getDealer(state: IState, accountNum: string): IDealer | undefined {
  return state.dealer.hash[accountNum];
}

export function isDealerLoading(state: IState, accountNum: string, loadingType: DealerLoadingTypes): boolean {
  return state.dealer.loading[accountNum] && state.dealer.loading[accountNum][loadingType];
}

export function getDealerFinancials(state: IState, accountNum: string): IDealerFinancialSummary[] {
  return state.dealer.financials[accountNum] ? state.dealer.financials[accountNum] : [];
}

export function getDealerOrderList(state: IState, accountNum: string, sortField: string = 'statusId'): IOrderMin[] {
  const salesIds = state.dealer.orderLists[accountNum] ? state.dealer.orderLists[accountNum] : [];

  const orders = salesIds.map<IOrderMin>(id => state.order.minOrderHash[id]);

  return sortBy(orders, sortField);
}

export function getDealerOrderTotalItems(state: IState, accountNum: string): number {
  return state.dealer.totalItems.orders[accountNum] ? state.dealer.totalItems.orders[accountNum] : 0;
}

export function getDealerShipmentTotalItems(state: IState, accountNum: string): number {
  return state.dealer.totalItems.shipments[accountNum] ? state.dealer.totalItems.shipments[accountNum] : 0;
}

export function getDealerShipments(state: IState, accountNum: string): IShipmentListItem[] {
  const {
    logistics: { shipmentListItemHash },
    dealer: { shipmentIds }
  } = state;

  const ids = shipmentIds[accountNum] ? shipmentIds[accountNum] : [];

  const shipments = ids.map(x => shipmentListItemHash[x]);

  return shipments;
}

export function getDealerAvailablePreseasonGroups(
  state: IState,
  values: { accountNum: string; fiscalYear: string; brand: string }
): IProgramGroup[] {
  const {
    dealer: { preseason },
    programs: { programGroups, programLevels }
  } = state;
  const { accountNum, fiscalYear, brand } = values;

  let groups: IProgramGroup[] = [];

  if (preseason[accountNum]) {
    if (preseason[accountNum][fiscalYear]) {
      if (preseason[accountNum][fiscalYear].availableProgramGroups) {
        const { availableProgramGroups } = preseason[accountNum][fiscalYear];

        if (availableProgramGroups[brand]) {
          groups = availableProgramGroups[brand].map<IProgramGroup>(id => ({
            access: programGroups[id] ? programGroups[id].access : [],
            brand: programGroups[id] ? programGroups[id].brand : '',
            fiscalYear: programGroups[id] ? programGroups[id].fiscalYear : '',
            levels: programGroups[id] ? programGroups[id].levelIds.map(x => programLevels[x]) : [],
            name: programGroups[id] ? programGroups[id].name : 'Unknown',
            programGroupId: id
          }));
        }
      }
    }
  }

  return groups;
}

export function getProgramTotal(
  state: IState,
  values: { accountNum: string; fiscalYear: string; brand: string }
): number {
  const {
    dealer: { preseason },
    programs: { programLevels }
  } = state;
  const { accountNum, fiscalYear } = values;

  let total = 0;

  if (preseason[accountNum]) {
    if (preseason[accountNum][fiscalYear]) {
      const levelIds = Object.values(preseason[accountNum][fiscalYear].selectedPrograms);

      total = levelIds.reduce((sum, id) => (sum += programLevels[id] ? programLevels[id].startAmount : 0), 0);
    }
  }

  return total;
}

export function getDealerPreseasonLevelId(
  state: IState,
  values: { accountNum: string; fiscalYear: string; brand: string }
): number | undefined {
  const {
    dealer: { preseason }
  } = state;
  const { accountNum, fiscalYear, brand } = values;

  let levelId: number | undefined;

  if (preseason[accountNum]) {
    if (preseason[accountNum][fiscalYear]) {
      if (preseason[accountNum][fiscalYear].selectedPrograms) {
        levelId = preseason[accountNum][fiscalYear].selectedPrograms[brand];
      }
    }
  }

  return levelId;
}

export function getPreseasonAmount(
  state: IState,
  values: { accountNum: string; fiscalYear: string; brand: string },
  valueType: FinancialType
): number | undefined {
  const {
    dealer: { preseason }
  } = state;
  const { accountNum, fiscalYear } = values;

  let value: number | undefined;

  if (preseason[accountNum]) {
    if (preseason[accountNum][fiscalYear]) {
      const { estimate, revised } = preseason[accountNum][fiscalYear];
      if (valueType === FinancialType.PreseasonEstimate && estimate) {
        value = estimate;
      } else if (valueType === FinancialType.PreseasonRevised && revised) {
        value = revised;
      }
    }
  }

  return value;
}

export function getPowerAssessments(
  { dealer: { dealerPowerAssessments, powerAssessments } }: IState,
  accountNum: string
): IPowerAssessment[] {
  const arr = dealerPowerAssessments[accountNum]
    ? dealerPowerAssessments[accountNum].map<IPowerAssessment>(x => powerAssessments[x])
    : [];

  return orderBy(arr, x => new Date(x.createdAtUtc), 'desc');
}

export function getDealerList(state: IState, full?: boolean): IDealerMin[] {
  const { dealerList } = state.dealer;

  if (full) {
    return dealerList;
  }

  const filters: ISalesDealersFilters = getFilters(state, FilterTypes.salesDealers);
  const sds = getFilteredSalesDistricts(state, filters);
  const st = filters.searchTerm.toLowerCase();

  return dealerList.filter(
    d =>
      (!filters.searchTerm || d.name.toLowerCase().includes(st) || d.location.toLowerCase().includes(st)) &&
      sds.includes(d.salesDistrict)
  );
}

export function getDealerListOptions(state: IState, returnType: 'accountNum' | 'businessEntityId'): IOption[] {
  return getDealerList(state, true).map<IOption>(d => ({
    label: d.name,
    secondaryLabel: d.location,
    value: d[returnType]
  }));
}

export function getDealerName(state: IState, accountNum: string): string {
  let name = state.dealer.hash[accountNum] ? state.dealer.hash[accountNum].name : accountNum;

  if (name === accountNum) {
    const dealer = state.dealer.dealerList.find(x => x.accountNum === accountNum);

    if (dealer) {
      name = dealer.name;
    }
  }

  return name;
}
